import { Search, X, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type {
  ActivityKindFilter,
  ActivityActorFilter,
} from "./activity-feed-types";

const KIND_OPTIONS: Array<{ value: ActivityKindFilter; label: string }> = [
  { value: "all", label: "All events" },
  { value: "state", label: "State changes" },
  { value: "comment", label: "Notes" },
  { value: "linked", label: "Integrations" },
];

const ACTOR_OPTIONS: Array<{ value: ActivityActorFilter; label: string }> = [
  { value: "all", label: "All sources" },
  { value: "user", label: "Team members" },
  { value: "run", label: "Walkthrough runs" },
  { value: "tracker", label: "Issue trackers" },
];

interface ActivityFilterBarProps {
  kind: ActivityKindFilter;
  onKindChange: (kind: ActivityKindFilter) => void;
  actor: ActivityActorFilter;
  onActorChange: (actor: ActivityActorFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onReset: () => void;
  totalLoaded: number;
  isLoading?: boolean;
}

export function ActivityFilterBar({
  kind,
  onKindChange,
  actor,
  onActorChange,
  search,
  onSearchChange,
  onReset,
  totalLoaded,
  isLoading = false,
}: ActivityFilterBarProps) {
  const hasActiveFilters =
    kind !== "all" || actor !== "all" || search.length > 0;
  const currentActorLabel =
    ACTOR_OPTIONS.find((a) => a.value === actor)?.label ?? "All sources";

  return (
    <div className="space-y-3">
      {/* Search and source select row */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && search) {
                e.preventDefault();
                onSearchChange("");
              }
            }}
            placeholder="Search by finding, note text, or team member…"
            className="h-9 rounded-sm pl-9 pr-8 text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-1 top-1/2 -translate-y-1/2 size-7 flex items-center justify-center text-muted-foreground hover:text-foreground rounded hover:bg-muted/50"
              aria-label="Clear search query"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "justify-between gap-2 shrink-0 text-xs font-medium",
                actor !== "all" &&
                  "border-foreground bg-accent text-accent-foreground font-semibold",
              )}
            >
              <span className="flex items-center gap-1.5">
                <Filter className="size-3 text-muted-foreground" />
                Source: {currentActorLabel}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {ACTOR_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => onActorChange(opt.value)}
                className={cn(
                  "text-xs cursor-pointer",
                  actor === opt.value && "font-semibold",
                )}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Kind pill tabs row */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1.5 border-t border-border/50">
        <div
          role="group"
          aria-label="Filter events by kind"
          className="flex flex-wrap items-center gap-1.5"
        >
          {KIND_OPTIONS.map((opt) => {
            const isActive = kind === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => onKindChange(opt.value)}
                className={cn(
                  "rounded-sm px-3 py-1 text-xs transition-colors shrink-0",
                  isActive
                    ? "bg-foreground text-background font-semibold shadow-xs"
                    : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-2xs text-muted-foreground">
          <span>{isLoading ? "Loading…" : `${totalLoaded} events loaded`}</span>
          {hasActiveFilters && (
            <>
              <span>·</span>
              <button
                type="button"
                onClick={onReset}
                className="text-foreground hover:underline font-medium"
              >
                Reset filters
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
